/**
 * 評価値の単位と悪手の基準。 評価値の大きさはゲームごとに違うので、 別のゲームの
 * 値と比べない。 `scale` は「大差」 の目安で、 グラフの上下端にも使う。
 *
 * 基準はチェスだけ実対局で確かめた仮の値。 他のゲームは同じ比率を当てている。
 */
import type { GameId } from '../../wasm';
import type { PositionAnalysis, StudyNode } from './tree';

interface Unit {
  label: string;
  divisor: number;
  digits: number;
  scale: number;
}

/** 解析に対応しているゲーム。 */
export const STUDY_GAMES: readonly GameId[] = ['Renju', 'AnimalShogi', 'Reversi', 'Chess', 'Jungo', 'Shogi', 'Backgammon', 'TwixT'];

const UNITS: Partial<Record<GameId, Unit>> = {
  Chess: { label: 'ポーン換算', divisor: 100, digits: 2, scale: 800 },
  Shogi: { label: '点', divisor: 1, digits: 0, scale: 3000 },
  Reversi: { label: '石', divisor: 100, digits: 1, scale: 1600 },
  AnimalShogi: { label: '点', divisor: 1, digits: 0, scale: 150 },
  Renju: { label: '点', divisor: 1, digits: 0, scale: 2000 },
  Jungo: { label: '点', divisor: 1, digits: 0, scale: 1000 },
  Backgammon: { label: '点', divisor: 1, digits: 0, scale: 800 },
  TwixT: { label: '点', divisor: 1, digits: 0, scale: 1000 },
};

const FALLBACK: Unit = { label: '点', divisor: 1, digits: 0, scale: 1000 };
const DOUBT_RATIO = 0.1875;
const BAD_RATIO = 0.375;
/** これ以上は読み切り。 */
export const MATE_SCORE = 1_000_000_000;

export function unitOf(game: GameId): Unit {
  return UNITS[game] ?? FALLBACK;
}

export function formatScore(game: GameId, score: number): string {
  const { divisor, digits } = unitOf(game);
  const v = (score / divisor).toFixed(digits);
  return score > 0 ? `+${v}` : score < 0 ? `−${v.slice(1)}` : v;
}

/** 手番側の値を先手から見た値にする。 */
export function firstView(a: PositionAnalysis, score: number): number {
  return a.side === 'First' ? score : -score;
}

export type Quality = 'bad' | 'doubt' | null;

export interface MoveReview {
  node: StudyNode;
  /** 1 始まりの手数。 */
  ply: number;
  side: 'First' | 'Second';
  quality: Quality;
  /** 指した側から見た損失。 */
  loss: number;
}

/**
 * 本譜の手を判定する。 同じ探索での最善手との差と、 次の局面との差の両方が基準を
 * 超えたときだけ印を付ける。 片方だけだと、 読みの深さの偶奇による振れを拾う。
 */
export function reviewMoves(
  game: GameId,
  line: StudyNode[],
  positions: (PositionAnalysis | undefined)[],
): MoveReview[] {
  const { scale } = unitOf(game);
  const out: MoveReview[] = [];
  line.forEach((node, i) => {
    const here = positions[i];
    const next = positions[i + 1];
    const best = here?.candidates[0];
    const nextBest = next?.candidates[0];
    if (!here || !best || !next || !nextBest) return;
    if (Math.abs(best.score) >= MATE_SCORE || Math.abs(nextBest.score) >= MATE_SCORE) return;
    const key = JSON.stringify(node.mv);
    const played = here.candidates.find((c) => JSON.stringify(c.mv) === key);
    if (!played) return;
    const searchLoss = best.score - played.score;
    const sign = here.side === next.side ? 1 : -1;
    const nextLoss = best.score - sign * nextBest.score;
    const loss = Math.min(searchLoss, nextLoss);
    const quality: Quality = loss >= scale * BAD_RATIO ? 'bad' : loss >= scale * DOUBT_RATIO ? 'doubt' : null;
    if (quality) out.push({ node, ply: i + 1, side: here.side, quality, loss });
  });
  return out;
}
